/**
 * Transparent, editable estimation model for the "Calculate My Savings"
 * calculator. Every number here is a published-average default that the
 * visitor can override; nothing is presented as a guaranteed outcome.
 */

export type MaintenanceLevel = "none" | "reactive" | "scheduled" | "proactive";

export type CountryData = {
  code: string;
  name: string;
  /** Specific yield: kWh produced per kWp installed per year (national average). */
  yieldPerKwp: number;
  /** Indicative value of energy in USD per kWh (PPA / merchant blend). */
  pricePerKwh: number;
  /** Grid emission factor in kg CO2e per kWh. */
  gridFactor: number;
  /** Regional multipliers applied to the national specific yield. */
  regions: { name: string; factor: number }[];
};

export const COUNTRIES: CountryData[] = [
  {
    code: "IL",
    name: "Israel",
    yieldPerKwp: 1680,
    pricePerKwh: 0.09,
    gridFactor: 0.51,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Negev / Arava", factor: 1.08 },
      { name: "Central / Sharon", factor: 1 },
      { name: "Galilee / North", factor: 0.94 },
    ],
  },
  {
    code: "US",
    name: "United States",
    yieldPerKwp: 1500,
    pricePerKwh: 0.075,
    gridFactor: 0.37,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Southwest", factor: 1.18 },
      { name: "Texas / South", factor: 1.05 },
      { name: "Midwest", factor: 0.93 },
      { name: "Northeast", factor: 0.85 },
    ],
  },
  {
    code: "ES",
    name: "Spain",
    yieldPerKwp: 1620,
    pricePerKwh: 0.07,
    gridFactor: 0.19,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Andalusia", factor: 1.07 },
      { name: "Castile / Central", factor: 1.02 },
      { name: "North / Atlantic", factor: 0.84 },
    ],
  },
  {
    code: "IT",
    name: "Italy",
    yieldPerKwp: 1400,
    pricePerKwh: 0.095,
    gridFactor: 0.26,
    regions: [
      { name: "National average", factor: 1 },
      { name: "South / Sicily", factor: 1.1 },
      { name: "Central", factor: 1 },
      { name: "North", factor: 0.88 },
    ],
  },
  {
    code: "DE",
    name: "Germany",
    yieldPerKwp: 1050,
    pricePerKwh: 0.08,
    gridFactor: 0.38,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Bavaria / South", factor: 1.07 },
      { name: "Central", factor: 1 },
      { name: "North", factor: 0.95 },
    ],
  },
  {
    code: "FR",
    name: "France",
    yieldPerKwp: 1250,
    pricePerKwh: 0.08,
    gridFactor: 0.06,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Provence / South", factor: 1.14 },
      { name: "Central", factor: 1 },
      { name: "North", factor: 0.86 },
    ],
  },
  {
    code: "GB",
    name: "United Kingdom",
    yieldPerKwp: 950,
    pricePerKwh: 0.09,
    gridFactor: 0.21,
    regions: [
      { name: "National average", factor: 1 },
      { name: "South England", factor: 1.08 },
      { name: "Midlands / Wales", factor: 1 },
      { name: "Scotland / North", factor: 0.9 },
    ],
  },
  {
    code: "GR",
    name: "Greece",
    yieldPerKwp: 1550,
    pricePerKwh: 0.075,
    gridFactor: 0.35,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Crete / Islands", factor: 1.06 },
      { name: "Mainland", factor: 1 },
    ],
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    yieldPerKwp: 1700,
    pricePerKwh: 0.045,
    gridFactor: 0.42,
    regions: [{ name: "National average", factor: 1 }],
  },
  {
    code: "SA",
    name: "Saudi Arabia",
    yieldPerKwp: 1750,
    pricePerKwh: 0.04,
    gridFactor: 0.55,
    regions: [{ name: "National average", factor: 1 }],
  },
  {
    code: "IN",
    name: "India",
    yieldPerKwp: 1550,
    pricePerKwh: 0.035,
    gridFactor: 0.71,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Rajasthan / Gujarat", factor: 1.1 },
      { name: "South", factor: 1 },
      { name: "East / North-East", factor: 0.9 },
    ],
  },
  {
    code: "AU",
    name: "Australia",
    yieldPerKwp: 1600,
    pricePerKwh: 0.06,
    gridFactor: 0.66,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Queensland / NT", factor: 1.08 },
      { name: "NSW / Victoria", factor: 0.96 },
    ],
  },
  {
    code: "BR",
    name: "Brazil",
    yieldPerKwp: 1550,
    pricePerKwh: 0.055,
    gridFactor: 0.12,
    regions: [
      { name: "National average", factor: 1 },
      { name: "North-East", factor: 1.1 },
      { name: "South-East", factor: 1 },
      { name: "South", factor: 0.92 },
    ],
  },
  {
    code: "CL",
    name: "Chile",
    yieldPerKwp: 1900,
    pricePerKwh: 0.05,
    gridFactor: 0.4,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Atacama / North", factor: 1.15 },
      { name: "Central", factor: 0.95 },
    ],
  },
  {
    code: "MX",
    name: "Mexico",
    yieldPerKwp: 1700,
    pricePerKwh: 0.055,
    gridFactor: 0.44,
    regions: [
      { name: "National average", factor: 1 },
      { name: "North", factor: 1.08 },
      { name: "Central / South", factor: 0.97 },
    ],
  },
  {
    code: "ZA",
    name: "South Africa",
    yieldPerKwp: 1750,
    pricePerKwh: 0.06,
    gridFactor: 0.9,
    regions: [
      { name: "National average", factor: 1 },
      { name: "Northern Cape", factor: 1.08 },
      { name: "Gauteng", factor: 1 },
      { name: "Coastal", factor: 0.94 },
    ],
  },
  {
    code: "NL",
    name: "Netherlands",
    yieldPerKwp: 980,
    pricePerKwh: 0.085,
    gridFactor: 0.33,
    regions: [{ name: "National average", factor: 1 }],
  },
  {
    code: "PT",
    name: "Portugal",
    yieldPerKwp: 1600,
    pricePerKwh: 0.07,
    gridFactor: 0.2,
    regions: [{ name: "National average", factor: 1 }],
  },
  {
    code: "JP",
    name: "Japan",
    yieldPerKwp: 1250,
    pricePerKwh: 0.1,
    gridFactor: 0.45,
    regions: [{ name: "National average", factor: 1 }],
  },
  {
    code: "CA",
    name: "Canada",
    yieldPerKwp: 1200,
    pricePerKwh: 0.07,
    gridFactor: 0.12,
    regions: [{ name: "National average", factor: 1 }],
  },
  {
    code: "OTHER",
    name: "Other / not listed",
    yieldPerKwp: 1400,
    pricePerKwh: 0.07,
    gridFactor: 0.4,
    regions: [{ name: "National average", factor: 1 }],
  },
];

export const getCountry = (code: string): CountryData =>
  COUNTRIES.find((country) => country.code === code) ?? COUNTRIES[COUNTRIES.length - 1];

/**
 * Recoverable performance gap by current maintenance practice, expressed as a
 * share of annual production. Ranges reflect published underperformance
 * studies, not a WoozyAI guarantee.
 */
export const MAINTENANCE_GAP: Record<MaintenanceLevel, { low: number; high: number }> = {
  none: { low: 0.06, high: 0.11 },
  reactive: { low: 0.045, high: 0.085 },
  scheduled: { low: 0.03, high: 0.06 },
  proactive: { low: 0.015, high: 0.035 },
};

export type LocationInput = {
  id: string;
  countryCode: string;
  region: string;
  /** Capacity in the selected unit (kW or MW). */
  capacity: number;
  sites: number;
};

export type CalculatorInput = {
  unit: "kW" | "MW";
  locations: LocationInput[];
  /** Optional actual annual production for the whole portfolio, in MWh. */
  annualProductionMwh?: number;
  /** Optional value of energy in USD per kWh, applied to the whole portfolio. */
  pricePerKwh?: number;
  maintenance: MaintenanceLevel;
  /** Share of recommendations the owner expects to implement (0-1). */
  implementation: number;
};

export type CalculatorResult = {
  totalCapacityKw: number;
  totalSites: number;
  /** Modelled or user-supplied baseline production, MWh per year. */
  baselineMwh: number;
  usedActualProduction: boolean;
  blendedYield: number;
  blendedPrice: number;
  extraMwhLow: number;
  extraMwhHigh: number;
  revenueLow: number;
  revenueHigh: number;
  co2TonnesLow: number;
  co2TonnesHigh: number;
};

const toKw = (capacity: number, unit: "kW" | "MW") => (unit === "MW" ? capacity * 1000 : capacity);

export const calculateSavings = (input: CalculatorInput): CalculatorResult => {
  const rows = input.locations.map((location) => {
    const country = getCountry(location.countryCode);
    const region = country.regions.find((item) => item.name === location.region) ?? country.regions[0];
    const capacityKw = Math.max(0, toKw(Number(location.capacity) || 0, input.unit));

    return {
      capacityKw,
      sites: Math.max(0, Math.round(Number(location.sites) || 0)),
      specificYield: country.yieldPerKwp * region.factor,
      price: country.pricePerKwh,
      gridFactor: country.gridFactor,
    };
  });

  const totalCapacityKw = rows.reduce((sum, row) => sum + row.capacityKw, 0);
  const totalSites = rows.reduce((sum, row) => sum + row.sites, 0);
  const modelledMwh = rows.reduce((sum, row) => sum + (row.capacityKw * row.specificYield) / 1000, 0);

  const weight = (pick: (row: (typeof rows)[number]) => number) =>
    totalCapacityKw > 0
      ? rows.reduce((sum, row) => sum + pick(row) * row.capacityKw, 0) / totalCapacityKw
      : pick(rows[0] ?? { capacityKw: 0, sites: 0, specificYield: 0, price: 0, gridFactor: 0 });

  const blendedYield = weight((row) => row.specificYield);
  const blendedGrid = weight((row) => row.gridFactor);
  const defaultPrice = weight((row) => row.price);

  const usedActualProduction = Boolean(input.annualProductionMwh && input.annualProductionMwh > 0);
  const baselineMwh = usedActualProduction ? (input.annualProductionMwh as number) : modelledMwh;
  const blendedPrice = input.pricePerKwh && input.pricePerKwh > 0 ? input.pricePerKwh : defaultPrice;

  const gap = MAINTENANCE_GAP[input.maintenance];
  const implementation = Math.min(1, Math.max(0, input.implementation));

  const extraMwhLow = baselineMwh * gap.low * implementation;
  const extraMwhHigh = baselineMwh * gap.high * implementation;

  return {
    totalCapacityKw,
    totalSites,
    baselineMwh,
    usedActualProduction,
    blendedYield,
    blendedPrice,
    extraMwhLow,
    extraMwhHigh,
    revenueLow: extraMwhLow * 1000 * blendedPrice,
    revenueHigh: extraMwhHigh * 1000 * blendedPrice,
    co2TonnesLow: (extraMwhLow * 1000 * blendedGrid) / 1000,
    co2TonnesHigh: (extraMwhHigh * 1000 * blendedGrid) / 1000,
  };
};
